import { TprmSchemaDisplayFormat } from '@evidentid/tprm-portal-lib/models/common/TprmSchemaDisplayFormat.model';
import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard';

import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';

export function camelToLabel(key: string): string {
    return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase())
        .trim();
}

export function getCountryLabel(code: any, enumLabels: CategorizedEnumLabels): string {
    const resolvedCode = code && typeof code === 'object' ? code.label || String(code) : String(code || '');
    const result = enumLabels?.countryCode?.[resolvedCode];
    if (result && typeof result === 'object') {
        return (result as any).label || resolvedCode;
    }
    return result || resolvedCode;
}

export function getInsuredValue(entity: Entity, key: string): any {
    return entity.insuredFields?.[key] ?? null;
}

export function hasInsuredValue(entity: Entity, key: string): boolean {
    const val = getInsuredValue(entity, key);
    return val !== null && val !== undefined && val !== '';
}

export function isAddressField(schema: any): boolean {
    return schema?.displayFormat === TprmSchemaDisplayFormat.address;
}

export function isArrayValue(entity: Entity, key: string): boolean {
    return Array.isArray(getInsuredValue(entity, key));
}

export function getAddressLines(entity: Entity, key: string, enumLabels: CategorizedEnumLabels): string[] {
    const addr: any = getInsuredValue(entity, key);
    if (!addr) {
        return [];
    }
    const lines: string[] = [];
    const line1 = [ addr.street, addr.aptSuiteOrUnit ].filter(Boolean).join(', ');
    if (line1) {
        lines.push(line1);
    }
    const line2 = [ addr.city, addr.state, addr.zipCode ].filter(Boolean).join(', ');
    if (line2) {
        lines.push(line2);
    }
    if (addr.country) {
        lines.push(getCountryLabel(addr.country, enumLabels));
    }
    return lines;
}

export function filteredArrayValue(entity: Entity, key: string, schema?: any): string[] {
    const val = getInsuredValue(entity, key);
    if (!Array.isArray(val)) {
        return [];
    }
    const filtered = val.filter((v: any) => v !== null && v !== undefined && v !== '');
    const itemEnum: any[] = schema?.items?.enum || [];
    const itemLabels: string[] = schema?.items?.enumLabels || [];
    if (itemEnum.length > 0 && itemLabels.length > 0) {
        return filtered.map((v: any) => {
            const idx = itemEnum.indexOf(v);
            return (idx >= 0 && itemLabels[idx]) ? itemLabels[idx] : String(v);
        });
    }
    return filtered.map((v: any) => String(v));
}

export function formatCustomValue(entity: Entity, key: string, schema: any, enumLabels: CategorizedEnumLabels): string {
    const val = getInsuredValue(entity, key);
    if (!hasInsuredValue(entity, key)) {
        return '—';
    }
    if (typeof val === 'boolean') {
        return val ? 'Yes' : 'No';
    }
    if (schema?.enum && schema?.enumTitles) {
        const idx = (schema.enum as any[]).indexOf(val);
        if (idx >= 0 && schema.enumTitles[idx]) {
            return schema.enumTitles[idx];
        }
    }
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
        const parts = [
            [ val.street, val.aptSuiteOrUnit ].filter(Boolean).join(', '),
            [ val.city, val.state, val.zipCode ].filter(Boolean).join(', '),
            val.country ? getCountryLabel(val.country, enumLabels) : '',
        ].filter(Boolean);
        return parts.join(', ') || '—';
    }
    return String(val);
}
